import { Card } from '@components/common/Card/Card';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { message } from 'antd';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useGetOrganizationsTreeQuery } from '@/api/organizations/api';
import { useGetRolesWithPermissionsQuery } from '@/api/roles/api';
import { RoleWithPermissionsResponse } from '@/api/roles/types';
import {
  useBlockUserMutation,
  useGetUserByIdQuery,
  useUnblockUserMutation,
  useUpdateUserMutation,
} from '@/api/users/api';
import type { UserRequest } from '@/api/users/types';
import { editModalConfig } from '@/pages/Administration/components/users/card/editFormSchema.config';
import { userFormSchema } from '@/pages/Administration/components/users/card/formSchema.config';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { administration } from '@/shared/security/permissions/administration';
import { UserStatus } from '@/shared/users/enums';

export const UserCard = () => {
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isUnblockModalOpen, setIsUnblockModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const { data: record, isLoading, isFetching } = useGetUserByIdQuery(id as string);

  const [blockUser, { isLoading: isBlocking }] = useBlockUserMutation();
  const [unblockUser, { isLoading: isUnblocking }] = useUnblockUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const { data: allRoles } = useGetRolesWithPermissionsQuery({});

  const { data: allOrganizations, isLoading: isOrganizationsLoading } =
    useGetOrganizationsTreeQuery();

  const handleBlock = async () => {
    try {
      setIsBlockModalOpen(false);
      await blockUser(id as string).unwrap();
      message.success('Пользователь успешно заблокирован');
      navigate('/administration/users');
    } catch (error) {
      console.error('Ошибка при блокировке пользователя:', error);
    }
  };

  const handleUnblock = async () => {
    try {
      setIsUnblockModalOpen(false);
      await unblockUser(id as string).unwrap();
      message.success('Пользователь успешно разблокирован');
      navigate('/administration/users');
    } catch (error) {
      console.error('Ошибка при разблокировке пользователя:', error);
    }
  };

  const handleEdit = async (values: UserRequest) => {
    try {
      await updateUser({ id: id as string, ...values }).unwrap();
      setIsEditModalOpen(false);
      message.success('Пользователь успешно сохранен');
    } catch (error) {
      console.error('Ошибка при обновлении пользователя:', error);
    }
  };

  const editFormData = record
    ? {
        firstName: record.firstName,
        middleName: record.middleName,
        lastName: record.lastName,
        email: record.email,
        organizationId: record.organization?.parentIdsWithThis
          ? record.organization?.parentIdsWithThis.slice(1)
          : undefined,
        position: record.position,
        phone: record.phone,
        roleNames: record.roleNames,
        sendStatusMail: record.sendStatusMail,
      }
    : undefined;

  const hideSendStatusMailRadio =
    !record?.hasPermissionReceiveStatusMail ||
    record?.organization?.level === OrganizationLevelType.STRUCTURAL_DIVISION;

  return (
    <>
      <Modal
        mode={'confirm'}
        open={isBlockModalOpen}
        onCancel={() => setIsBlockModalOpen(false)}
        onSubmit={handleBlock}
        confirmLoading={isBlocking}
        text={'Вы уверены, что хотите заблокировать пользователя?'}
        submitText={'Да'}
        cancelText={'Нет'}
      />

      <Modal
        mode={'confirm'}
        open={isUnblockModalOpen}
        onCancel={() => setIsUnblockModalOpen(false)}
        onSubmit={handleUnblock}
        confirmLoading={isUnblocking}
        text={'Вы уверены, что хотите разблокировать пользователя?'}
        submitText={'Да'}
        cancelText={'Нет'}
      />

      <Modal
        mode={'form'}
        open={isEditModalOpen}
        title={'Редактировать пользователя'}
        schema={editModalConfig(
          allOrganizations,
          allRoles as RoleWithPermissionsResponse[],
        )}
        data={editFormData}
        formKey={'editUser'}
        destroyOnHidden={true}
        onSubmit={handleEdit}
        onCancel={() => setIsEditModalOpen(false)}
        confirmLoading={isUpdating}
        buttons={{
          submit: { text: 'Сохранить', type: 'primary' },
          cancel: { text: 'Отмена', type: 'default' },
        }}
      />
      <PageTitle title={'Общие сведения о пользователе'} />
      <Card
        viewMode
        loading={isLoading || isFetching || isOrganizationsLoading}
        schema={userFormSchema(
          allRoles as RoleWithPermissionsResponse[],
          hideSendStatusMailRadio,
        )}
        data={{
          ...record,
          organizationName: record?.organization?.name,
          sendStatusMail: record?.sendStatusMail ? 'Да' : 'Нет',
        }}
        actions={{
          onBack: () => navigate(-1),
          ...(record?.status === UserStatus.ACTIVE && {
            onEdit: {
              action: () => setIsEditModalOpen(true),
              security: {
                permissions: administration.users.edit,
              },
            },
          }),
          extra: [
            ...(record?.status === UserStatus.ACTIVE
              ? [
                  {
                    text: 'Заблокировать',
                    action: () => setIsBlockModalOpen(true),
                    security: {
                      permissions: administration.users.block,
                    },
                  },
                ]
              : []),
            ...(record?.status === UserStatus.BLOCKED
              ? [
                  {
                    text: 'Разблокировать',
                    action: () => setIsUnblockModalOpen(true),
                    security: {
                      permissions: administration.users.unblock,
                    },
                  },
                ]
              : []),
          ],
        }}
      />
    </>
  );
};
